import type { AppSettings, MusicTrackId } from "../lib/storage";
import type { CombinationKind } from "@guandan/game-core";

export const MUSIC_TRACKS: readonly {
  readonly id: MusicTrackId;
  readonly label: string;
  readonly description: string;
}[] = [
  {
    id: "haikou-night",
    label: "海口夜色",
    description: "舒展的海风与夜景氛围",
  },
  { id: "jade-breeze", label: "玉桌清风", description: "轻快、克制的牌桌节奏" },
  { id: "city-glow", label: "城市微光", description: "温暖的现代会客厅氛围" },
] as const;

export type GameSoundEffect = "play" | "pass" | "turn" | "bomb" | "settle";
export type CharacterVoiceGender = "woman" | "man";

export function combinationCallout(kind: CombinationKind): string | null {
  if (kind === "bomb" || kind === "joker-bomb") return "我炸！";
  if (kind === "straight-flush") return "哈哈，同花顺！";
  return null;
}

interface TrackDefinition {
  readonly bpm: number;
  readonly root: number;
  readonly lead: readonly number[];
  readonly bass: readonly number[];
  readonly wave: OscillatorType;
}

const TRACK_DEFINITIONS: Readonly<Record<MusicTrackId, TrackDefinition>> = {
  "haikou-night": {
    bpm: 84,
    root: 57,
    lead: [0, -1, 7, -1, 10, -1, 7, -1, 3, -1, 5, -1, 7, -1, 3, -1],
    bass: [0, -1, -1, -1, 3, -1, -1, -1, 5, -1, -1, -1, 3, -1, -1, -1],
    wave: "sine",
  },
  "jade-breeze": {
    bpm: 104,
    root: 60,
    lead: [0, 3, 5, 7, 5, 3, 0, -1, 7, 10, 7, 5, 3, 5, 3, -1],
    bass: [0, -1, 0, -1, 5, -1, 5, -1, 3, -1, 3, -1, 5, -1, 7, -1],
    wave: "triangle",
  },
  "city-glow": {
    bpm: 92,
    root: 55,
    lead: [7, -1, 10, 12, 10, -1, 7, 5, 3, -1, 5, 7, 5, -1, 3, 0],
    bass: [0, -1, -1, -1, 5, -1, -1, -1, 3, -1, -1, -1, 7, -1, -1, -1],
    wave: "sine",
  },
};

export interface SoundscapeSettings {
  readonly music: boolean;
  readonly soundEffects: boolean;
  readonly musicTrack: MusicTrackId;
  readonly musicVolume: number;
  readonly soundEffectsVolume: number;
}

export function soundscapeSettings(settings: AppSettings): SoundscapeSettings {
  return {
    music: settings.music && !settings.customMusicTrackId,
    soundEffects: settings.soundEffects,
    musicTrack: settings.musicTrack,
    musicVolume: clamp01(settings.musicVolume),
    soundEffectsVolume: clamp01(settings.soundEffectsVolume),
  };
}

export class GameSoundscape {
  private context: AudioContext | null = null;
  private musicTimer: number | null = null;
  private nextStepAt = 0;
  private step = 0;
  private settings: SoundscapeSettings;

  constructor(settings: SoundscapeSettings) {
    this.settings = settings;
  }

  applySettings(settings: SoundscapeSettings) {
    const trackChanged = settings.musicTrack !== this.settings.musicTrack;
    this.settings = settings;
    if (trackChanged) {
      this.step = 0;
      this.nextStepAt = this.context?.currentTime ?? 0;
    }
    if (!settings.music) this.stopMusic();
    else if (this.context?.state === "running") this.startMusic();
  }

  async unlock() {
    const context = this.ensureContext();
    if (context.state !== "running") await context.resume();
    if (this.settings.music) this.startMusic();
  }

  play(effect: GameSoundEffect) {
    if (!this.settings.soundEffects) return;
    const context = this.context;
    if (!context || context.state !== "running") return;
    const now = context.currentTime;
    const volume = this.settings.soundEffectsVolume;
    if (effect === "play") {
      this.noiseBurst(now, 0.13, volume * 0.18);
      this.tone(now, 176, 0.08, volume * 0.1, "triangle", 132);
    } else if (effect === "pass") {
      this.tone(now, 246, 0.18, volume * 0.11, "sine", 164);
    } else if (effect === "turn") {
      this.tone(now, 659, 0.12, volume * 0.09, "sine");
      this.tone(now + 0.1, 880, 0.18, volume * 0.08, "sine");
    } else if (effect === "bomb") {
      this.noiseBurst(now, 0.32, volume * 0.24);
      this.tone(now, 116, 0.34, volume * 0.22, "sawtooth", 58);
    } else {
      [523, 659, 784, 1047].forEach((frequency, index) =>
        this.tone(now + index * 0.12, frequency, 0.28, volume * 0.08, "sine"),
      );
    }
  }

  announceCombination(kind: CombinationKind, gender: CharacterVoiceGender) {
    const text = combinationCallout(kind);
    if (text) this.announcePhrase(text, gender);
  }

  announceAntiTribute(gender: CharacterVoiceGender) {
    this.announcePhrase("哈哈，抗贡！这把稳了！", gender);
  }

  announceLowHand(gender: CharacterVoiceGender) {
    this.announcePhrase("哈哈，报十张哟！", gender);
  }

  private announcePhrase(text: string, gender: CharacterVoiceGender) {
    if (!this.settings.soundEffects || typeof window === "undefined") return;
    if (
      !("speechSynthesis" in window) ||
      typeof SpeechSynthesisUtterance === "undefined"
    )
      return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "zh-CN";
    utterance.rate = 1.04;
    utterance.pitch = gender === "woman" ? 1.18 : 0.82;
    utterance.volume = this.settings.soundEffectsVolume;
    const voices = window.speechSynthesis.getVoices();
    const chineseVoices = voices.filter((voice) =>
      /^zh(?:-|_)/iu.test(voice.lang),
    );
    const preferredNames =
      gender === "woman"
        ? /tingting|meijia|xiaoxiao|female|女/iu
        : /sinji|yunxi|yunyang|male|男/iu;
    utterance.voice =
      chineseVoices.find((voice) => preferredNames.test(voice.name)) ??
      chineseVoices[0] ??
      null;
    window.speechSynthesis.speak(utterance);
  }

  async dispose() {
    this.stopMusic();
    if (typeof window !== "undefined" && "speechSynthesis" in window)
      window.speechSynthesis.cancel();
    const context = this.context;
    this.context = null;
    if (context && context.state !== "closed") await context.close();
  }

  private ensureContext(): AudioContext {
    if (!this.context)
      this.context = new AudioContext({ latencyHint: "interactive" });
    return this.context;
  }

  private startMusic() {
    if (this.musicTimer !== null || !this.context || !this.settings.music)
      return;
    this.nextStepAt = Math.max(
      this.nextStepAt,
      this.context.currentTime + 0.04,
    );
    const schedule = () => this.scheduleMusicWindow();
    schedule();
    this.musicTimer = window.setInterval(schedule, 100);
  }

  private stopMusic() {
    if (this.musicTimer !== null) window.clearInterval(this.musicTimer);
    this.musicTimer = null;
  }

  private scheduleMusicWindow() {
    const context = this.context;
    if (!context || context.state !== "running" || !this.settings.music) return;
    const track = TRACK_DEFINITIONS[this.settings.musicTrack];
    const stepDuration = 60 / track.bpm / 2;
    while (this.nextStepAt < context.currentTime + 0.24) {
      const index = this.step % track.lead.length;
      const lead = track.lead[index] ?? -1;
      const bass = track.bass[index] ?? -1;
      if (lead >= 0) {
        this.tone(
          this.nextStepAt,
          midiFrequency(track.root + lead),
          stepDuration * 0.8,
          this.settings.musicVolume * 0.09,
          track.wave,
        );
      }
      if (bass >= 0) {
        this.tone(
          this.nextStepAt,
          midiFrequency(track.root - 24 + bass),
          stepDuration * 1.7,
          this.settings.musicVolume * 0.055,
          "sine",
        );
      }
      this.step += 1;
      this.nextStepAt += stepDuration;
    }
  }

  private tone(
    at: number,
    frequency: number,
    duration: number,
    volume: number,
    wave: OscillatorType,
    endFrequency = frequency,
  ) {
    const context = this.context;
    if (!context || volume <= 0) return;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = wave;
    oscillator.frequency.setValueAtTime(frequency, at);
    oscillator.frequency.exponentialRampToValueAtTime(
      Math.max(20, endFrequency),
      at + duration,
    );
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(
      Math.max(0.0001, volume),
      at + 0.018,
    );
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(at);
    oscillator.stop(at + duration + 0.02);
  }

  private noiseBurst(at: number, duration: number, volume: number) {
    const context = this.context;
    if (!context || volume <= 0) return;
    const length = Math.ceil(context.sampleRate * duration);
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < data.length; index += 1) {
      data[index] = (Math.random() * 2 - 1) * (1 - index / data.length);
    }
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    filter.type = "bandpass";
    filter.frequency.value = 1250;
    filter.Q.value = 0.7;
    gain.gain.setValueAtTime(Math.max(0.0001, volume), at);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    source.buffer = buffer;
    source.connect(filter).connect(gain).connect(context.destination);
    source.start(at);
    source.stop(at + duration);
  }
}

function clamp01(value: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0.5;
}

function midiFrequency(note: number): number {
  return 440 * 2 ** ((note - 69) / 12);
}
